<template>
  <CModal :show="show" @update:show="onShowChange" size="xl" class="login-message-modal " add-content-classes="border-radius-1">
    <template #header-wrapper>
      <div></div>
    </template>

    <div class="message-modal__header mb-4">
      <div class="message-modal__hero">
        <label class="h4 mb-1">{{ title }}</label>
        <div class="text-muted">
          {{ isEditMode ? $t('setting.status.form.editHint') : $t('setting.status.form.createHint') }}
        </div>
      </div>
    </div>

    <ModalLanguageToolbar :languages="languages" :active-tab="activeLanguageTab" :active-lang="activeLang" :label-resolver="langLabel" @update:activeTab="activeLanguageTab = $event" />

    <CRow>
      <CCol md="7" col="12">
        <CCard class="bg-style2 h-100 panel-card content-panel-card">
          <CCardBody class="content-panel-body">
            <h5 class="mb-3 panel-title">{{ $t('common.sections.content') }}</h5>

            <div class="content-block mb-3">
              <CPInput
                v-model.trim="activeTitle"
                :label="$t('setting.status.form.fields.title')"
                :required="true"
                placeholder=""
              />
            </div>
            <div class="content-block mb-0">
              <CPQuillEditor
                v-model="activeDescription"
                :label="$t('common.description')"
                placeholder=""
              />
            </div>
          </CCardBody>
        </CCard>
      </CCol>

      <CCol md="5" col="12">
        <CCard class="bg-style2 h-100 panel-card">
          <CCardBody>
            <h5 class="mb-3 panel-title">{{ $t('common.sections.settings') }}</h5>

            <div class="setting-field mb-3">
              <CPSelect
                v-model="groupSelected"
                :options="groupOptions"
                :label="$t('setting.status.form.fields.group')"
                :required="true"
                label-key="label"
                track-by="value"
                :multiple="false"
                :close-on-select="true"
                :show-labels="false"
                :allow-empty="false"
                :select-label="''"
                :deselect-label="''"
                placeholder=""
              />
            </div>

            <div class="setting-field mb-3">
              <CPInput
                v-model="keyValue"
                :label="$t('setting.status.form.fields.key')"
                :required="true"
                placeholder=""
              />
            </div>

            <div class="setting-field mb-0">
              <CPSelect
                v-model="stateSelected"
                :options="stateOptions"
                :label="$t('setting.status.form.fields.state')"
                :required="true"
                label-key="label"
                track-by="value"
                :multiple="false"
                :close-on-select="true"
                :show-labels="false"
                :allow-empty="false"
                :select-label="''"
                :deselect-label="''"
                placeholder=""
              />
            </div>
            <AuditInfoCard
              v-if="isEditMode"
              :title="$t('common.audit.created')"
              icon="cil-user-follow"
              :by="createdByLabel"
              :at="createdAtLabel"
            />
            <AuditInfoCard
              v-if="isEditMode"
              :title="$t('common.audit.updated')"
              icon="cil-history"
              :by="updatedByLabel"
              :at="updatedAtLabel"
            />
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

      <div class="modal-footer-wrap d-flex w-100 justify-content-end mt-3">
        <CButton class="mr-2 footer-btn-cancel footer-action-btn" color="danger" variant="outline" shape="pill" @click="onCancel">
          <CIcon name="cil-ban" class="mr-1 action-icon" />
          <span class="font-weight-bold pr-1 pl-1"> {{ $t('common.actions.cancel') }} </span>
        </CButton>
        <CButton color="success" shape="pill" class="footer-btn-save footer-action-btn" @click="onSubmit">
          <CIcon :name="submitIcon" class="mr-1 action-icon" />
          <span class="font-weight-bold pr-1 pl-1"> {{ submitLabel }} </span>
        </CButton>
      </div>
    <template #footer-wrapper>
      <div></div>
    </template>

  </CModal>
</template>

<script>
import CPInput from '@/projects/components/custom/CPInput'
import CPSelect from '@/projects/components/custom/CPSelect'
import CPQuillEditor from '@/projects/components/custom/CPQuillEditor'
import ModalLanguageToolbar from '@/projects/components/layout/ModalLanguageToolbar'
import RequiredValidation from '@/projects/mixins/requiredValidation'
import AuditInfoCard from './AuditInfoCard'

function normalizeItems (items) {
  if (!Array.isArray(items)) return []
  return items
    .map(item => ({
      key: item && item.key ? String(item.key).trim().toLowerCase() : '',
      value: item && item.value ? String(item.value) : ''
    }))
    .filter(item => item.key)
}

function createEmptyForm () {
  return {
    _id: null,
    groupId: '',
    key: '',
    titleItems: [{ key: 'th', value: '' }, { key: 'en', value: '' }],
    descriptionItems: [{ key: 'th', value: '' }, { key: 'en', value: '' }],
    state: true
  }
}

export default {
  name: 'StatusFormModal',
  components: { CPInput, CPSelect, CPQuillEditor, ModalLanguageToolbar, AuditInfoCard },
  mixins: [RequiredValidation],
  props: {
    show: { type: Boolean, default: false },
    title: { type: String, default: '' },
    groups: { type: Array, default: () => [] },
    value: {
      type: Object,
      default: () => ({
        _id: null,
        groupId: '',
        key: '',
        titleItems: [{ key: 'th', value: '' }, { key: 'en', value: '' }],
        descriptionItems: [{ key: 'th', value: '' }, { key: 'en', value: '' }],
        state: true
      })
    }
  },
  data () {
    return {
      localForm: createEmptyForm(),
      activeLang: 'th',
    }
  },
  computed: {
    isEditMode () {
      return !!(this.localForm && this.localForm._id)
    },
    submitLabel () {
      return this.isEditMode ? this.$t('common.actions.update') : this.$t('common.actions.save')
    },
    submitIcon () {
      return this.isEditMode ? 'cil-pencil' : 'cil-save'
    },
    createdByLabel () {
      return this.value && this.value.createdByName ? this.value.createdByName : '-'
    },
    createdAtLabel () {
      if (!(this.value && this.value.createdAt)) return '-'
      const date = new Date(this.value.createdAt)
      if (Number.isNaN(date.getTime())) return this.value.createdAt
      return date.toLocaleString()
    },
    updatedByLabel () {
      return this.value && this.value.updatedByName ? this.value.updatedByName : '-'
    },
    updatedAtLabel () {
      if (!(this.value && this.value.updatedAt)) return '-'
      const date = new Date(this.value.updatedAt)
      if (Number.isNaN(date.getTime())) return this.value.updatedAt
      return date.toLocaleString()
    },
    languages () {
      const titleKeys = this.localForm.titleItems.map(item => item.key)
      const descKeys = this.localForm.descriptionItems.map(item => item.key)
      const merged = [...new Set([...titleKeys, ...descKeys].filter(Boolean))]
      return merged.length ? merged : ['th', 'en']
    },
    stateOptions () {
      return [
        { value: 'active', label: this.$t('common.status.active') },
        { value: 'inactive', label: this.$t('common.status.inactive') }
      ]
    },
    activeLanguageTab: {
      get () {
        const idx = this.languages.indexOf(this.activeLang)
        return idx >= 0 ? idx : 0
      },
      set (tabIndex) {
        this.activeLang = this.languages[tabIndex] || this.languages[0] || 'th'
      }
    },
    activeTitle: {
      get () {
        const found = this.localForm.titleItems.find(item => item.key === this.activeLang)
        return found ? found.value : ''
      },
      set (value) {
        this.upsertTitle(this.activeLang, value)
      }
    },
    activeDescription: {
      get () {
        const found = this.localForm.descriptionItems.find(item => item.key === this.activeLang)
        return found ? found.value : ''
      },
      set (value) {
        this.upsertDescription(this.activeLang, value)
      }
    },
    stateSelected: {
      get () {
        return this.localForm.state ? this.stateOptions[0] : this.stateOptions[1]
      },
      set (option) {
        this.localForm.state = !option || option.value !== 'inactive'
      }
    },
    groupOptions () {
      return (this.groups || [])
        .map(item => ({
          value: item && item._id ? String(item._id) : '',
          label: item && item.name ? String(item.name) : (item && item.key ? String(item.key) : '')
        }))
        .filter(item => item.value)
    },
    groupSelected: {
      get () {
        return this.groupOptions.find(item => item.value === this.localForm.groupId) || null
      },
      set (option) {
        this.localForm.groupId = option && option.value ? String(option.value) : ''
      }
    },
    keyValue: {
      get () {
        return this.localForm.key || ''
      },
      set (value) {
        this.localForm.key = String(value || '')
          .toUpperCase()
          .replace(/\s+/g, '_')
          .replace(/[^A-Z0-9_]/g, '')
      }
    }
  },
  watch: {
    show: {
      immediate: true,
      handler (value) {
        if (value) this.resetForm()
      }
    },
    value: {
      deep: true,
      handler () {
        if (this.show) this.resetForm()
      }
    }
  },
  methods: {
    langLabel (lang) {
      const mapping = { th: this.$t('setting.languages.th'), en: this.$t('setting.languages.en') }
      return mapping[lang] || lang
    },
    upsertTitle (lang, value) {
      const idx = this.localForm.titleItems.findIndex(item => item.key === lang)
      if (idx >= 0) {
        this.localForm.titleItems[idx].value = value
      } else {
        this.localForm.titleItems.push({ key: lang, value })
      }
    },
    upsertDescription (lang, value) {
      const idx = this.localForm.descriptionItems.findIndex(item => item.key === lang)
      if (idx >= 0) {
        this.localForm.descriptionItems[idx].value = value
      } else {
        this.localForm.descriptionItems.push({ key: lang, value })
      }
    },
    resetForm () {
      const titleItems = normalizeItems(this.value && this.value.titleItems)
      const descriptionItems = normalizeItems(this.value && this.value.descriptionItems)

      this.localForm = {
        _id: this.value && this.value._id ? this.value._id : null,
        groupId: this.value && this.value.groupId ? String(this.value.groupId) : (this.groupOptions[0] ? this.groupOptions[0].value : ''),
        key: this.value && this.value.key ? String(this.value.key) : '',
        titleItems: titleItems.length ? titleItems : [{ key: 'th', value: '' }, { key: 'en', value: '' }],
        descriptionItems: descriptionItems.length ? descriptionItems : [{ key: 'th', value: '' }, { key: 'en', value: '' }],
        state: typeof (this.value && this.value.state) === 'boolean' ? this.value.state : true
      }

      this.activeLang = this.languages[0] || 'th'
    },
    onShowChange (value) {
      this.$emit('update:show', value)
      if (!value) this.$emit('cancel')
    },
    onCancel () {
      this.$emit('update:show', false)
      this.$emit('cancel')
    },
    onSubmit () {
      if (!this.validateRequired()) {
        this.$emit('invalid', this.$t('setting.status.form.validation.required'))
        return
      }

      const title = normalizeItems(this.localForm.titleItems)
        .filter(item => item.value && String(item.value).trim())
      const description = normalizeItems(this.localForm.descriptionItems)
        .filter(item => item.value && String(item.value).trim())

      if (!title.length) {
        this.$emit('invalid', this.$t('setting.status.form.validation.title'))
        return
      }
      if (!this.localForm.groupId) {
        this.$emit('invalid', this.$t('setting.status.form.validation.group'))
        return
      }
      if (!String(this.localForm.key || '').trim()) {
        this.$emit('invalid', this.$t('setting.status.form.validation.key'))
        return
      }

      this.$emit('submit', {
        _id: this.localForm._id,
        groupId: this.localForm.groupId,
        key: String(this.localForm.key || '').trim(),
        title,
        description,
        state: !!this.localForm.state
      })
    }
  }
}
</script>
